import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, BellOff, BellRing, Check, Loader2, Radio, Wifi, WifiOff } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { ALERT_KIND, CLASS_COLORS, fmtK, fmtTimeMYT } from "@/lib/traffic";
import { cn } from "@/lib/utils";

export function AlertBanner({ alerts, onAck, compact, wall }) {
  const open = (alerts || []).filter((a) => !a.acked_at);
  if (!open.length) return null;
  const ack = async (a) => {
    try {
      await api.post(`/traffic/alerts/${a.id}/ack`);
      toast.success("Alert acknowledged");
      onAck?.();
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  const tone = (a) => (a.severity === "critical" ? "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200"
    : a.severity === "info" ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200"
      : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200");
  const Icon = ({ a }) => (a.kind === "radar_online" ? <Wifi className="h-4 w-4 shrink-0" /> : a.kind.includes("offline") ? <WifiOff className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />);
  if (wall) {
    // wall-display pages (Overview, Live) keep one slim line so the 16:9 layout never scrolls
    const a = open[0];
    return (
      <div className={cn("flex items-center gap-2 rounded-xl border px-3 py-1 text-xs", tone(a))} data-testid="alert-banner">
        <Icon a={a} />
        <div className="min-w-0 flex-1 truncate" title={a.message} data-testid={`alert-banner-${a.id}`}><span className="font-semibold">{ALERT_KIND[a.kind] || a.kind}</span><span className="mx-1.5 opacity-50">·</span>{a.message}</div>
        <span className="num hidden shrink-0 opacity-70 sm:inline">{fmtTimeMYT(a.opened_at)}</span>
        {open.length > 1 && <Link to="/alerts" className="shrink-0 font-medium underline-offset-2 hover:underline" data-testid="alert-banner-more">+{open.length - 1} more</Link>}
        <button onClick={() => ack(a)} data-testid={`acknowledge-alert-btn-${a.id}`} aria-label="Acknowledge alert" className="inline-flex shrink-0 items-center gap-1 rounded-full bg-background/80 px-2 py-0.5 font-medium shadow-sm hover:bg-background"><Check className="h-3 w-3" /> Ack</button>
      </div>
    );
  }
  return (
    <div className="min-w-0 space-y-2" data-testid="alert-banner">
      <AnimatePresence initial={false}>
        {open.slice(0, compact ? 2 : 5).map((a) => (
          <motion.div key={a.id} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }} data-testid={`alert-banner-${a.id}`}
            className={cn("flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm", tone(a))}>
            <Icon a={a} />
            <div className="min-w-0 flex-1 truncate" title={a.message}>
              <span className="font-semibold">{ALERT_KIND[a.kind] || a.kind}</span>
              <span className="mx-1.5 opacity-50">·</span>
              <span>{a.message}</span>
            </div>
            <span className="num hidden shrink-0 text-xs opacity-70 sm:inline">{fmtTimeMYT(a.opened_at)} MYT</span>
            <button onClick={() => ack(a)} data-testid={`acknowledge-alert-btn-${a.id}`} aria-label="Acknowledge alert"
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-background/80 px-2.5 py-1 text-xs font-medium shadow-sm transition-colors hover:bg-background">
              <Check className="h-3.5 w-3.5" /><span className="hidden sm:inline">Acknowledge</span>
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
      {open.length > (compact ? 2 : 5) && (
        <Link to="/alerts" className="block text-xs text-muted-foreground hover:text-foreground" data-testid="alert-banner-more">+{open.length - (compact ? 2 : 5)} more open alerts</Link>
      )}
    </div>
  );
}

export function SoundToggle({ enabled, onToggle }) {
  return (
    <button onClick={onToggle} data-testid="sound-alert-toggle" aria-label="Toggle alert sound" title={enabled ? "Alert chime on" : "Alert chime muted"}
      className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
      {enabled ? <BellRing className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
    </button>
  );
}

// Shown while the backend rebuilds traffic statistics from stored history (after a publish that changes how rows are counted).
export function RebuildBanner({ rebuild }) {
  if (!rebuild) return null;
  const running = rebuild.status === "running";
  const pct = rebuild.pct;
  const stage = rebuild.table === "statistics" ? "finalising daily totals" : rebuild.table ? `processing ${rebuild.table}` : "starting";
  return (
    <div className={cn("rounded-2xl border px-4 py-3 text-sm", running ? "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200"
      : rebuild.status === "error" ? "border-rose-200 bg-rose-50 text-rose-900" : "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200")}
      data-testid="rebuild-banner">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          <span className="font-semibold">{running ? "Rebuilding traffic statistics" : rebuild.status === "error" ? "Statistics rebuild failed" : "Statistics rebuilt"}</span>
          <span className="opacity-80">{running ? `· ${stage}` : rebuild.error || "· history, analytics and forecast are up to date"}</span>
        </div>
        <span className="num text-xs" data-testid="rebuild-progress-text">
          {fmtK(rebuild.rows)}{rebuild.total_rows ? ` / ${fmtK(rebuild.total_rows)} rows` : " rows"}{pct != null ? ` · ${pct}%` : ""}
        </span>
      </div>
      {running && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-sky-200/60 dark:bg-sky-900/60">
          <div className="h-full rounded-full bg-sky-600 transition-[width] duration-700 dark:bg-sky-400" style={{ width: `${pct ?? 5}%` }} data-testid="rebuild-progress-bar" />
        </div>
      )}
      {running && <p className="mt-1 text-xs opacity-80">Live status keeps working. Historical charts, comparisons and the forecast fill in as the rebuild progresses.</p>}
    </div>
  );
}

export function LivePing({ online, label }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/70 px-2.5 py-1 text-xs" data-testid="live-ping">
      <span className="relative flex h-2 w-2">
        {online && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />}
        <span className={cn("relative inline-flex h-2 w-2 rounded-full", online ? "bg-emerald-500" : "bg-slate-400")} />
      </span>
      <span className={online ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}>{label || (online ? "LIVE" : "OFFLINE")}</span>
    </span>
  );
}

// Shown while the Edge sender is catching up: queued batches after an outage, or the one-off initial export.
export function BacklogBadge({ feed }) {
  if (!feed?.online) return null;
  const exporting = feed.phase && !/^live/i.test(feed.phase) && feed.export_pct != null && feed.export_pct < 100;
  if (!exporting && !(feed.queue_items > 0)) return null;
  const text = exporting
    ? `${feed.phase} · ${feed.export_pct}%`
    : `Sending backlog · ${feed.queue_items} batch${feed.queue_items === 1 ? "" : "es"}${feed.queue_mb >= 0.1 ? ` · ${feed.queue_mb} MB` : ""}`;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-700 dark:text-amber-300"
      title="The sender stores rows on disk whenever the dashboard is unreachable and uploads them as soon as it reconnects. Live data keeps flowing in parallel."
      data-testid="backlog-badge">
      <Loader2 className="h-3 w-3 animate-spin" />
      <span className="num">{text}</span>
    </span>
  );
}

export function Kpi({ label, value, sub, tone, testId, icon: Icon, compact }) {
  return (
    <div className={cn("surface relative overflow-hidden", compact ? "p-4" : "p-5")} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="eyebrow">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className={cn("font-semibold tracking-tight num", compact ? "mt-1 text-2xl 2xl:text-3xl" : "mt-2 text-3xl")}>{value}</div>
      {sub && <div className={cn("mt-1 text-xs", tone === "up" ? "text-emerald-600" : tone === "down" ? "text-rose-600" : "text-muted-foreground")}>{sub}</div>}
    </div>
  );
}

export function LiveTicker({ rows, perSite, fill }) {
  const total = Object.values(perSite || {}).reduce((a, b) => a + b, 0);
  return (
    <div data-testid="live-ticker" className={cn(fill && "flex min-h-0 flex-1 flex-col")}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><Radio className="h-4 w-4 text-emerald-500" /> Vehicles coming in</div>
        <span className="num text-xs text-muted-foreground">{fmtK(total)} in the last 60 s</span>
      </div>
      <ul className={cn("mt-3 space-y-1.5 overflow-hidden", fill ? "min-h-0 flex-1" : "max-h-[380px]")}>
        <AnimatePresence initial={false}>
          {(rows || []).slice(0, fill ? 24 : 14).map((r) => (
            <motion.li key={r.id} layout initial={{ opacity: 0, x: -12, backgroundColor: "rgba(16,185,129,0.18)" }} animate={{ opacity: 1, x: 0, backgroundColor: "rgba(0,0,0,0)" }}
              exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="flex items-center gap-3 rounded-xl px-2 py-1.5 text-xs" data-testid="live-ticker-row">
              <span className="num w-14 shrink-0 text-muted-foreground">{r.ts.slice(11, 19)}</span>
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: CLASS_COLORS[r.cls] || "#94a3b8" }} />
              <span className="min-w-0 flex-1 truncate font-medium">{r.name || r.site || "Unmapped radar"}</span>
              <span className="w-8 shrink-0 text-muted-foreground">{r.dir || "—"}</span>
              <span className="w-7 shrink-0 text-muted-foreground">{r.lane || ""}</span>
              <span className="num w-20 shrink-0 whitespace-nowrap text-right font-semibold">{r.speed} <span className="font-normal text-muted-foreground">km/h</span></span>
            </motion.li>
          ))}
        </AnimatePresence>
        {!rows?.length && <li className="py-6 text-center text-xs text-muted-foreground">Waiting for vehicle passages…</li>}
      </ul>
    </div>
  );
}

export function Seg({ value, onChange, options, testId }) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto rounded-full bg-secondary p-0.5" data-testid={testId}>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)} data-testid={`${testId}-${o.value}`}
          className={cn("whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors", value === o.value ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
